import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
	AfterViewInit,
	Component,
	computed,
	inject,
	signal,
	TemplateRef,
	viewChild,
	ViewEncapsulation,
} from '@angular/core';
import {
	Router,
	RouterLink,
	RouterLinkActive,
	RouterOutlet,
} from '@angular/router';
import {
	ChemicalService,
	ChemicalUsageLogFormDialogComponent,
} from '@context/frontend/chemical';
import {
	APP_VERSION,
	DestroyService,
	DialogService,
	HelpDialogComponent,
} from '@context/frontend/common';
import { ContentService } from '@context/frontend/content';
import {
	LogFormDialogComponent,
	LogFormDialogDismiss,
} from '@context/frontend/log';
import { getUaDevice } from '@context/frontend/native/platform';
import { NotificationService } from '@context/frontend/notification';
import { DetailMenuDropdownComponent } from '@context/frontend/ui/components';
import {
	AuthService,
	AvatarComponent,
	LogoutDialogComponent,
	OrganizationSelectorDialogComponent,
	RoleAccessPipe,
	UserNamePipe,
} from '@context/frontend/user';
import { DetailMenuItem } from '@context/shared/types/common';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiTabBar } from '@taiga-ui/addon-mobile';
import {
	TuiButton,
	TuiDialogContext,
	TuiDropdown,
	TuiIcon,
	TuiLink,
} from '@taiga-ui/core';
import { TuiBadgedContent, TuiBadgeNotification } from '@taiga-ui/kit';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import * as Mousetrap from 'mousetrap';
import { map, Subscription } from 'rxjs';
import { MobileMenuDialogComponent } from '../../dialogs';
import { GlobalSearchBarComponent } from '../global-search-bar/global-search-bar.component';
import { GlobalSearchDialogComponent } from '../global-search-dialog/global-search-dialog.component';
import {
	EntityType,
	EntityTypeMenuItems,
	MenuItem,
	MenuItems,
} from './navigation.const';

@Component({
	imports: [
		RouterOutlet,
		TuiButton,
		TuiIcon,
		TranslocoModule,
		RouterLink,
		RouterLinkActive,
		TuiLink,
		RoleAccessPipe,
		AvatarComponent,
		UserNamePipe,
		TuiTabBar,
		TuiBadgedContent,
		TuiBadgeNotification,
		AsyncPipe,
		NgTemplateOutlet,
		GlobalSearchBarComponent,
		TuiDropdown,
		DetailMenuDropdownComponent,
	],
	selector: 'ctx-navigation-template',
	templateUrl: 'navigation-template.component.html',
	styleUrl: 'navigation-template.component.scss',
	providers: [DestroyService],
	encapsulation: ViewEncapsulation.None,
	host: {
		'[attr.device]': 'device',
		'[class.collapsed]': 'sidebarCollapsed()',
	},
})
export class NavigationTemplateComponent implements AfterViewInit {
	readonly device = getUaDevice();
	readonly sidebarCollapsed = signal(false);

	readonly createTemplate =
		viewChild<TemplateRef<TuiDialogContext>>('createTemplate');
	readonly accountDropdown =
		viewChild<TemplateRef<unknown>>('accountDropdown');

	readonly authService = inject(AuthService);
	private readonly notificationService = inject(NotificationService);
	private readonly router = inject(Router);
	private readonly dialog = inject(DialogService);
	private readonly contentService = inject(ContentService);
	private readonly chemicalService = inject(ChemicalService);
	readonly version = inject(APP_VERSION);

	readonly accountMenuOpen = signal(false);

	readonly entityTypes = EntityTypeMenuItems;
	readonly menuItems = MenuItems({
		createHandler: () => this.onPromptCreate(),
		logoutHandler: () => this.onPromptLogout(),
		notificationCount$: this.notificationService.unreadNotificationCount$
			.asObservable()
			.pipe(
				map((count) => {
					if (!count) return null;
					if (count >= 100) return `${count}+`;
					return `${count}`;
				}),
			),
	});

	/** Menu items with profile hidden for super admins (supers have no org profile). */
	get filteredMenuItems(): MenuItem[] {
		const isSuper = this.authService.authUser?.role === 'super';
		if (!isSuper) return this.menuItems as MenuItem[];
		return (this.menuItems as MenuItem[]).filter(
			(item) => item.key !== 'profile',
		);
	}

	readonly switchOrganizationMenuItem: MenuItem = {
		key: 'switch-organization',
		icon: '@tui.building-2',
		handler: () => this.onSwitchOrganization(),
		location: 'mobile',
	};

	/** Check if user can switch organizations (Super Admin OR multiple organizations). */
	get canSwitchOrganization(): boolean {
		const authUser = this.authService.authUser;
		if (!authUser) return false;

		// Super admins can switch organizations
		if (authUser.role === 'super') return true;

		// Regular users can switch if they have multiple organizations
		if (authUser.role === 'user' && authUser.users) {
			return authUser.users.length > 1;
		}

		return false;
	}

	/** Account menu items for the dropdown. */
	readonly accountMenuItems = computed<DetailMenuItem[]>(() => {
		const items: DetailMenuItem[] = [];

		// Add switch organization option if user can switch
		if (this.canSwitchOrganization) {
			items.push({
				text: 'select-organization.title',
				iconStart: '@tui.building',
				handler: () => this.onSwitchOrganization(),
			});
		}

		// Sign out is always available
		items.push({
			text: 'navigation.sign-out',
			iconStart: '@tui.log-out',
			handler: () => this.onPromptLogout(),
		});

		return items;
	});

	readonly createLogDialog = () =>
		this.dialog.open<LogFormDialogDismiss>(
			new PolymorpheusComponent(LogFormDialogComponent),
			LogFormDialogComponent.Options({
				data: {
					fileId: this.contentService.openFileId.value ?? undefined,
				},
			}),
		);

	readonly helpItem: MenuItem = {
		key: 'help',
		icon: 'circle-help',
		handler: () => this.onOpenHelp(),
		location: 'side',
	};

	readonly signOutItem: MenuItem = {
		key: 'sign-out',
		icon: '@tui.log-out',
		handler: () => this.onPromptLogout(),
		location: 'side',
	};

	createDialogSubscription: Subscription | null = null;

	readonly helpDialog = () =>
		this.dialog.open(
			new PolymorpheusComponent(HelpDialogComponent),
			HelpDialogComponent.Options({
				data: {
					user: this.authService.user,
					organization: this.authService.organization,
				},
			}),
		);

	readonly searchDialog = () =>
		this.dialog.open(
			new PolymorpheusComponent(GlobalSearchDialogComponent),
			GlobalSearchDialogComponent.Options(),
		);

	readonly logoutDialog = this.dialog.open<boolean>(
		new PolymorpheusComponent(LogoutDialogComponent),
		LogoutDialogComponent.Options(),
	);

	readonly mobileMenuDialog = (menuItems: MenuItem[]) =>
		this.dialog.open(
			new PolymorpheusComponent(MobileMenuDialogComponent),
			MobileMenuDialogComponent.Options({ data: { menuItems } }),
		);

	ngAfterViewInit() {
		this.setupShortcuts();
	}

	setupShortcuts() {
		const globalSearch = document.getElementById('app-global-search-bar');
		if (globalSearch) {
			Mousetrap.bind('mod+k', () => {
				globalSearch.focus();
			});
		}
	}

	readonly onMenuOpen = () =>
		this.mobileMenuDialog([
			...this.filteredMenuItems,
			...(this.canSwitchOrganization
				? [this.switchOrganizationMenuItem]
				: []),
			this.signOutItem,
			...(this.authService.authUser?.role !== 'super'
				? [this.helpItem]
				: []),
		]).subscribe();

	readonly onOpenHelp = () => this.helpDialog().subscribe();

	readonly onDismissLogout = (logout: boolean) =>
		logout ? this.router.navigate(['/auth/logout']) : {};

	readonly onPromptLogout = () => {
		this.accountMenuOpen.set(false);
		this.logoutDialog.subscribe({
			next: (data) => this.onDismissLogout(data),
		});
	};

	readonly onSwitchOrganization = () => {
		this.accountMenuOpen.set(false);
		this.organizationSelectorDialog().subscribe({
			next: (org) => {
				if (org) {
					// Organization was selected and switched
					// The dialog handles the organization switch internally
					// Reload the page to ensure all components reflect the new organization
					window.location.reload();
				}
			},
		});
	};

	readonly organizationSelectorDialog = () =>
		this.dialog.open<{ id: string } | null>(
			new PolymorpheusComponent(OrganizationSelectorDialogComponent),
			OrganizationSelectorDialogComponent.Options(),
		);

	readonly onToggleSidebar = () =>
		this.sidebarCollapsed.update((value) => !value);

	onPromptCreate() {
		const template = this.createTemplate();
		if (!template) return;
		this.createDialogSubscription = this.dialog
			.open(template, {
				label: 'navigation.create',
				size: 's',
			})
			.subscribe({
				complete: () => {
					this.createDialogSubscription = null;
				},
			});
	}

	onCreate(entityType: EntityType) {
		this.createDialogSubscription?.unsubscribe();
		switch (entityType) {
			case 'user':
				return this.router.navigate(['users', 'invite']);
			case 'chemical':
				return this.router.navigate(['chemicals', 'catalog', 'create']);
			case 'chemical-log': {
				const data: {
					fileId?: string;
					chemicalId?: string;
				} = {};
				const fileId = this.contentService.openFileId.value;
				if (fileId) data.fileId = fileId;
				const chemicalId = this.chemicalService.openChemicalId.value;
				if (chemicalId) data.chemicalId = chemicalId;
				return void this.dialog
					.open(
						new PolymorpheusComponent(
							ChemicalUsageLogFormDialogComponent,
						),
						ChemicalUsageLogFormDialogComponent.Options({
							data,
						}),
					)
					.subscribe();
			}
			case 'file': {
				const queryParams: Record<string, string> = {};
				const folderId = this.contentService.openFolderId.value;
				if (folderId) queryParams['folder'] = folderId;
				return this.router.navigate(['home', 'file', 'create'], {
					queryParams,
				});
			}
			case 'folder': {
				const queryParams: Record<string, string> = {};
				const folderId = this.contentService.openFolderId.value;
				if (folderId) queryParams['folder'] = folderId;
				return this.router.navigate(['home', 'folder', 'create'], {
					queryParams,
				});
			}
			case 'log':
				return void this.onCreateLogFlow();
			default:
				console.warn('Type not supported', entityType);
				return;
		}
	}

	readonly onCreateLogFlow = () => {
		this.createLogDialog().subscribe({
			next: (data) => {
				if (!data || !Array.isArray(data)) return;
				const first = data[0];
				this.router.navigate(['home', 'file', first.parentContentId], {
					queryParams: { log: first.log.id },
				});
			},
		});
	};

	readonly onSearchOpen = () => this.searchDialog().subscribe();
}
